import React from 'react';
import {
  BarChart3,
  CheckSquare,
  Clock,
  GraduationCap,
  Lightbulb,
  ListOrdered,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import type { LeanLibraryItem } from '../../types/leanLibrary';
import {
  LEAN_IEL_IMPACT_LABELS,
  LEAN_MATURITY_LABELS,
  LEAN_NIVEL_LABELS,
} from '../../types/leanLibrary';
import { getConsultantView, isEnrichedRoutine } from '../../services/leanLibraryEnrichment';

const IEL_IMPACT_COLOR: Record<string, string> = {
  baixo: 'bg-slate-100 text-slate-600',
  medio: 'bg-amber-50 text-amber-700',
  alto: 'bg-blue-50 text-blue-700',
  muito_alto: 'bg-emerald-50 text-emerald-700',
};

const MATURITY_COLOR: Record<string, string> = {
  basica: 'bg-slate-100 text-slate-700',
  intermediaria: 'bg-blue-50 text-blue-700',
  avancada: 'bg-violet-50 text-violet-700',
  excelencia: 'bg-emerald-50 text-emerald-800',
};

interface LeanLibraryConsultantPanelProps {
  item: LeanLibraryItem;
  onApply: () => void;
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-100 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-900/30">
      <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-400">
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

export const LeanLibraryConsultantPanel: React.FC<LeanLibraryConsultantPanelProps> = ({
  item,
  onApply,
}) => {
  const view = getConsultantView(item);
  const enriched = isEnrichedRoutine(item.id);

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {enriched ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                <Sparkles size={10} />
                Consultor completo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                <Lightbulb size={10} />
                Consultor em expansão
              </span>
            )}
            {view.nivelMaturidade && (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${MATURITY_COLOR[view.nivelMaturidade]}`}
              >
                {LEAN_MATURITY_LABELS[view.nivelMaturidade]}
              </span>
            )}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600">
              {item.categoria}
            </span>
          </div>
          <h2 className="text-[15px] font-bold text-slate-900 dark:text-slate-100">{item.titulo}</h2>
          <p className="mt-1 text-[11px] text-slate-500">{item.descricao}</p>
        </div>

        <Section icon={<Target size={12} />} title="Objetivo">
          <p className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">{item.objetivo}</p>
        </Section>

        <Section icon={<ListOrdered size={12} />} title="Como executar">
          <ol className="space-y-1.5">
            {view.swiPassos.map((passo, idx) => (
              <li key={idx} className="flex gap-2 text-[12px] text-slate-700 dark:text-slate-300">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[10px] font-bold text-blue-700">
                  {idx + 1}
                </span>
                <span className="pt-0.5 leading-relaxed">{passo}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section icon={<CheckSquare size={12} />} title="Itens de conformidade">
          <p className="mb-2 text-[10px] text-slate-500">
            O processo auditado estava conforme? (não avalia se o líder executou corretamente)
          </p>
          <ul className="space-y-1">
            {view.itensConformidade.map((itemConf, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-[12px] text-slate-700 dark:text-slate-300"
              >
                <span className="mt-0.5 text-slate-400">□</span>
                {itemConf}
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={<BarChart3 size={12} />} title="Indicadores sugeridos">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase text-slate-500">Primários</p>
              <ul className="space-y-0.5">
                {view.indicadoresPrimarios.map((ind) => (
                  <li key={ind} className="text-[12px] text-slate-700 dark:text-slate-300">
                    • {ind}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase text-slate-500">Secundários</p>
              <ul className="space-y-0.5">
                {view.indicadoresSecundarios.map((ind) => (
                  <li key={ind} className="text-[12px] text-slate-600 dark:text-slate-400">
                    • {ind}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        <Section icon={<TrendingUp size={12} />} title="Relação causal">
          <p className="text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
            {view.relacaoCausal}
          </p>
        </Section>

        {view.ielImpacto && (
          <Section icon={<GraduationCap size={12} />} title="Impacto no IEL">
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['Disciplina', view.ielImpacto.disciplina],
                  ['Aderência ao Padrão', view.ielImpacto.aderenciaPadrao],
                  ['Resultado', view.ielImpacto.resultado],
                ] as const
              ).map(([label, level]) => (
                <div key={label} className="text-center">
                  <p className="mb-1 text-[9px] font-medium text-slate-500">{label}</p>
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${IEL_IMPACT_COLOR[level]}`}
                  >
                    {LEAN_IEL_IMPACT_LABELS[level]}
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section icon={<Clock size={12} />} title="Frequência recomendada (benchmark)">
          <div className="space-y-1">
            {Object.entries(view.frequenciaBenchmark).map(([nivel, freq]) => (
              <div
                key={nivel}
                className="flex items-center justify-between text-[11px] text-slate-700 dark:text-slate-300"
              >
                <span className="text-slate-500">{LEAN_NIVEL_LABELS[nivel as keyof typeof LEAN_NIVEL_LABELS]}</span>
                <span className="font-semibold text-blue-700">{freq}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <footer className="shrink-0 border-t border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-[var(--bg-surface)]">
        <button
          type="button"
          onClick={onApply}
          className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700"
        >
          Aplicar Modelo
        </button>
        <p className="mt-2 text-center text-[10px] text-slate-400">
          Preenche SWI, checklist de conformidade e indicador sugerido — ajuste horário e salve.
        </p>
      </footer>
    </div>
  );
};

export default LeanLibraryConsultantPanel;
